import { inject, Injectable } from '@angular/core';
import { ApiHttpService } from '../../api-http.service';
import {
  ClientReturn,
  ClientReturnSendInput,
  ClientReturnSendPrefill,
  CreateReturnFromComplaint,
  CustomHttpResponse,
} from '../../../models';
import { catchError, map, Observable, take, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ClientReturnService {
  apiService = inject(ApiHttpService<ClientReturn>);

  get(iri: string): Observable<ClientReturn> {
    return this.apiService.get(iri).pipe(
      take(1),
      map((result: CustomHttpResponse<ClientReturn>) => result.body),
    );
  }

  getById(id: string): Observable<ClientReturn> {
    return this.apiService.get(`/client_returns/${id}`).pipe(
      take(1),
      map((result: CustomHttpResponse<ClientReturn>) => result.body),
      catchError(() => {
        return throwError(() => new Error('ENTITY_NOT_FOUND'));
      }),
    );
  }

  /** Client returns whose document number contains the given text (the API filter is a case-insensitive partial match). */
  findByDocumentNumber(documentNumber: string): Observable<ClientReturn[]> {
    return this.apiService.getMany(`/client_returns?documentNumber=${encodeURIComponent(documentNumber)}`).pipe(
      take(1),
      map((result) => result.body['hydra:member']),
    );
  }

  create(clientReturn: ClientReturn): Observable<ClientReturn> {
    return this.apiService
      .post('/client_returns', clientReturn)
      .pipe(map((result: CustomHttpResponse<ClientReturn>) => result.body));
  }

  createFromComplaint(createFromComplaint: CreateReturnFromComplaint): Observable<ClientReturn> {
    return this.apiService
      .post('/client_returns/from_complaint', createFromComplaint)
      .pipe(map((result: CustomHttpResponse<ClientReturn>) => result.body));
  }

  update(iri: string, clientReturn: ClientReturn): Observable<ClientReturn> {
    return this.apiService
      .patch(iri, clientReturn)
      .pipe(map((result: CustomHttpResponse<ClientReturn>) => result.body));
  }

  downloadPdf(iri: string) {
    return this.apiService.downloadFileAsBlob(`${iri}/download_pdf`);
  }

  /** The mail the API proposes for sending the return to the client. */
  getSendPrefill(iri: string): Observable<ClientReturnSendPrefill> {
    return this.apiService.get(`${iri}/send_prefill`).pipe(
      take(1),
      map((result: CustomHttpResponse<ClientReturnSendPrefill>) => result.body),
    );
  }

  /** Mails the return to the client. The mail goes out in the background; the return gets its lastSentOn once it is sent. */
  send(iri: string, input: ClientReturnSendInput): Observable<ClientReturn> {
    return this.apiService
      .post(`${iri}/send`, input)
      .pipe(map((result: CustomHttpResponse<ClientReturn>) => result.body));
  }
}
